import type {
    Control,
    FieldErrors,
    FieldValues,
    Path,
    UseFormRegister,
} from 'react-hook-form';
import type { StackProps } from 'tamagui';

import { formatTextInputAsDate } from '../../utils/dateUtils';
import { DateInput } from '../inputs/DateInput';
import type { InputProps } from '../inputs/Input';
import {
    ControlledFormattedInput,
    type FormatterFunction,
} from './ControlledFormattedInput';

const createDateFormatter = (): FormatterFunction => {
    return (input: string, previousValue?: string) => {
        const prevValue = previousValue ?? '';
        const formattedValue = formatTextInputAsDate(prevValue, input);
        return {
            displayValue: formattedValue,
            storedValue: formattedValue,
        };
    };
};

interface ControlledDateInputProps<
    TFieldValues extends FieldValues,
    TContext,
> extends Omit<InputProps, 'formatValueOnChange'> {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
    label?: string;
    hint?: string;
    containerStyle?: StackProps;
    rightSideContent?: React.ReactNode;
}

export const ControlledDateInput = <
    TFieldValues extends FieldValues,
    TContext,
>({
    ...props
}: ControlledDateInputProps<TFieldValues, TContext>) => {
    return (
        <ControlledFormattedInput
            {...props}
            formatter={createDateFormatter()}
            InputComponent={DateInput}
            inputComponentProps={{
                placeholder: props.placeholder ?? 'MM/DD/YYYY',
            }}
        />
    );
};
